import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

const NavigationContext = createContext();

const STORAGE_KEY = "nav-active-panel"; // "", "before", "during", "after"

function setHashPanel(panel) {
  if (typeof window === "undefined") return;
  window.location.hash = panel ? panel : "";
}

function getHashPanel() {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash || "";
  const value = raw.replace(/^#/, "");
  return value || null;
}

function clearHashPanel() {
  if (typeof window === "undefined") return;
  const { pathname, search } = window.location;
  window.history.replaceState({}, document.title, pathname + search);
}

function getDuringHashForIndex(idx) {
  switch (idx) {
    case 0:
      return "during";
    case 1:
      return "during-weather";
    case 2:
      return "during-transportation";
    case 3:
      return "during-utilities";
    case 4:
      return "during-aid";
    default:
      return "during";
  }
}

export function NavigationProvider({ children }) {
  const [roadmapOpen, setRoadmapOpen] = useState(false);
  const [checklistsOpen, setChecklistsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState("");
  const [sitRepPanelIndex, setSitRepPanelIndex] = useState(0);
  const [openHub, setOpenHub] = useState(false);
  const [openTimeline, setTimelineOpen] = useState(false);
  const [openHubFlag, setOpenHubFlag] = useState(false);

  const [weatherAlertSidebarOpen, setWeatherAlertSidebarOpen] = useState(false);
  const [weatherAlertForSidebar, setWeatherAlertForSidebar] = useState(null);

  const [highlightChecklist, setHighlightChecklist] = useState(false);
  const [highlightRoadmap, setHighlightRoadmap] = useState(false);
  const [highlightSupplyLists, setHighlightSupplyLists] = useState(false);

  const [highlightHubNavIndex, setHighlightHubNavIndex] = useState(null);

  const [roadmapCenterRequested, setRoadmapCenterRequested] = useState(false);
  const [beforeScrollRequested, setBeforeScrollRequested] = useState(false);
  const [lastBeforeTarget, setLastBeforeTarget] = useState(null);

  const [beforeActiveTile, setBeforeActiveTile] = useState(null);

  const [duringScrollRequested, setDuringScrollRequested] = useState(false);
  const [duringSubIndex, setDuringSubIndex] = useState(0);

  const [highlightAfterTimeline, setHighlightAfterTimeline] = useState(false);
  const [highlightAfterTips, setHighlightAfterTips] = useState(false);
  const [highlightAfterChecklists, setHighlightAfterChecklists] =
    useState(false);
  const [lastAfterTarget, setLastAfterTarget] = useState(null);
  const [afterTargetPanelId, setAfterTargetPanelId] = useState(null);
  const [afterScrollRequested, setAfterScrollRequested] = useState(false);

  const [flashBeforeRow, setFlashBeforeRow] = useState(false);
  const [flashDuringRow, setFlashDuringRow] = useState(false);
  const [flashAfterRow, setFlashAfterRow] = useState(false);

  const [hashHubFlashIndex, setHashHubFlashIndex] = useState(null);

  const [updatesCategory, setUpdatesCategory] = useState(null);
  const [updatesUtilityType, setUpdatesUtilityType] = useState(null);
  const [updatesFocusRequest, setUpdatesFocusRequest] = useState(0);
  const [updatesTab, setUpdatesTab] = useState(null);

  const [duringPanelScrollRequest, setDuringPanelScrollRequest] = useState(0);
  const [duringPanelScrollTarget, setDuringPanelScrollTarget] = useState(null);

  const trackNavigation = useCallback((eventName, params = {}) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, {
        event_category: "Navigation",
        ...params,
      });
    }
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      // console.log("GA event:", eventName, params);
    }
  }, []);

  const openExclusive = useCallback((panelKey) => {
    if (panelKey === "before") {
      setRoadmapOpen(true);
      setOpenHub(false);
      setTimelineOpen(false);
    } else if (panelKey === "during") {
      setRoadmapOpen(false);
      setOpenHub(true);
      setTimelineOpen(false);
    } else if (panelKey === "after") {
      setRoadmapOpen(false);
      setOpenHub(false);
      setTimelineOpen(true);
    } else {
      setRoadmapOpen(false);
      setOpenHub(false);
      setTimelineOpen(false);
    }
    setActivePanel(panelKey || "");
  }, []);

  const openBeforePlain = useCallback(
    (source = "unknown") => {
      openExclusive("before");
      setBeforeScrollRequested(true);

      if (source === "navrail-top") {
        setFlashBeforeRow(true);
      }

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("before");
      }

      trackNavigation("open_before_plain", { source });
    },
    [openExclusive, trackNavigation],
  );

  const openBeforeGeneric = useCallback(
    (source = "unknown") => {
      openExclusive("before");
      setLastBeforeTarget("quick-safety-tips-and-tools");
      setBeforeScrollRequested(true);

      if (source === "navrail-top") {
        setFlashBeforeRow(true);
      }

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("before");
      }

      trackNavigation("open_before_generic", { source });
    },
    [openExclusive, trackNavigation],
  );

  const openBeforeLearnAndShare = useCallback(
    (source = "unknown") => {
      openExclusive("before");
      setLastBeforeTarget("learn-and-share");
      setBeforeScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("before-learn-and-share");
      }

      trackNavigation("open_before_learn_and_share", { source });
    },
    [openExclusive, trackNavigation],
  );

  const openRoadmap = useCallback(
    (source = "unknown") => {
      openExclusive("before");
      setRoadmapCenterRequested(true);
      setHighlightRoadmap(true);
      setLastBeforeTarget("readiness-roadmap");
      setBeforeScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("before-readiness-roadmap");
      }

      trackNavigation("open_roadmap", { source });
    },
    [openExclusive, trackNavigation],
  );

  const openBeforeChecklists = useCallback(
    (source = "unknown") => {
      openExclusive("before");
      setChecklistsOpen(true);
      setHighlightChecklist(true);
      setLastBeforeTarget("preparedness-checklists");
      setBeforeScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("before-hazard-prep-checklists");
      }

      trackNavigation("open_before_checklists", { source });
    },
    [openExclusive, trackNavigation],
  );

  const openBeforeSupplyLists = useCallback(
    (source = "unknown") => {
      openExclusive("before");
      setChecklistsOpen(true);
      setHighlightSupplyLists(true);
      setLastBeforeTarget("supply-lists");
      setBeforeScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("before-supply-kits");
      }

      trackNavigation("open_before_supply_lists", { source });
    },
    [openExclusive, trackNavigation],
  );

  const openEmergencyHub = useCallback(
    (source = "unknown", subIndex = 0) => {
      openExclusive("during");
      setSitRepPanelIndex(subIndex);
      setDuringSubIndex(subIndex);
      setDuringScrollRequested(true);

      setDuringPanelScrollTarget(subIndex);
      setDuringPanelScrollRequest((prev) => prev + 1);

      if (source === "navrail-top") {
        setFlashDuringRow(true);
      }

      if (source !== "deep_link" && source !== "hashchange") {
        if (typeof window !== "undefined") {
          const current = (window.location.hash || "").replace(/^#/, "");
          const isWeatherChip = current.startsWith("local-conditions-");
          if (!isWeatherChip) {
            let hash;
            if (source === "navrail-submenu" && subIndex === 0) {
              hash = "during-updates";
            } else {
              hash = getDuringHashForIndex(subIndex);
            }
            setHashPanel(hash);
          }
        }
      }

      trackNavigation("open_hub", { source, subIndex });
    },
    [openExclusive, trackNavigation],
  );

  const openUpdatesUtilitiesWater = useCallback(
    (source = "unknown") => {
      openEmergencyHub(source, 0);
      setUpdatesCategory("Utilities");
      setUpdatesUtilityType("Water");
      setUpdatesFocusRequest((prev) => prev + 1);

      trackNavigation("open_updates_utilities_water", { source });
    },
    [openEmergencyHub, trackNavigation],
  );

  const clearUpdatesNavigation = useCallback(() => {
    setUpdatesCategory(null);
    setUpdatesUtilityType(null);
    setUpdatesTab(null);
  }, []);

  const openAfterPlain = useCallback(
    (source = "unknown") => {
      openExclusive("after");
      setLastAfterTarget(null);
      setAfterTargetPanelId(null);
      setAfterScrollRequested(true);

      if (source === "navrail-top") {
        setFlashAfterRow(true);
      }

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("after");
      }

      trackNavigation("open_after_plain", { source });
    },
    [openExclusive, trackNavigation],
  );

  const openTimelinePanel = useCallback(
    (source = "unknown") => {
      openExclusive("after");
      setLastAfterTarget("recovery-timeline");
      setAfterTargetPanelId(null);
      setTimeout(() => {
        setAfterTargetPanelId("recovery-timeline");
      }, 0);
      setAfterScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("after");
      }

      trackNavigation("open_timeline", { source });
    },
    [openExclusive, trackNavigation],
  );

  const openAfterMentalHealth = useCallback(
    (source = "unknown") => {
      openExclusive("after");
      setHighlightAfterTips(true);
      setLastAfterTarget("mental-health-resources");
      setAfterTargetPanelId(null);
      setTimeout(() => {
        setAfterTargetPanelId("mental-health-resources");
      }, 0);
      setAfterScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("after-mental-health-and-well-being");
      }

      trackNavigation("open_after_mental_health", { source });
    },
    [openExclusive, trackNavigation],
  );

  const openAfterTimeline = useCallback(
    (source = "unknown") => {
      openExclusive("after");
      setHighlightAfterTimeline(true);
      setLastAfterTarget("recovery-timeline");
      setAfterTargetPanelId(null);
      setTimeout(() => {
        setAfterTargetPanelId("recovery-timeline");
      }, 0);
      setAfterScrollRequested(true);

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("after-recovery-timeline");
      }

      trackNavigation("open_after_timeline", { source });
    },
    [openExclusive, trackNavigation],
  );

  const openAfterChecklists = useCallback(
    (source = "unknown") => {
      openExclusive("after");
      setHighlightAfterChecklists(true);

      setLastAfterTarget("aid");
      setAfterTargetPanelId("recovery-checklists");

      if (source !== "deep_link" && source !== "hashchange") {
        setHashPanel("aid");
      }

      trackNavigation("open_after_checklists", { source });
    },
    [openExclusive, trackNavigation],
  );

  const openWeatherAlertSidebar = useCallback(
    (alert, source = "unknown") => {
      setWeatherAlertForSidebar(alert);
      setWeatherAlertSidebarOpen(true);
      trackNavigation("open_weather_alert_sidebar", {
        source,
        event: alert?.properties?.event,
        severity: alert?.properties?.severity,
      });
    },
    [trackNavigation],
  );

  const closeWeatherAlertSidebar = useCallback(
    (source = "unknown") => {
      setWeatherAlertSidebarOpen(false);
      trackNavigation("close_weather_alert_sidebar", { source });
    },
    [trackNavigation],
  );

  const closePanels = useCallback(() => {
    setRoadmapOpen(false);
    setChecklistsOpen(false);
    setOpenHub(false);
    setTimelineOpen(false);
    setActivePanel("");
    setWeatherAlertSidebarOpen(false);
    setWeatherAlertForSidebar(null);
    setHighlightChecklist(false);
    setHighlightRoadmap(false);
    setHighlightSupplyLists(false);
    setHighlightHubNavIndex(null);
    setRoadmapCenterRequested(false);
    setBeforeScrollRequested(false);
    setDuringScrollRequested(false);
    setHighlightAfterTips(false);
    setHighlightAfterTimeline(false);
    setHighlightAfterChecklists(false);
    setAfterTargetPanelId(null);
    setAfterScrollRequested(false);
    setFlashBeforeRow(false);
    setFlashDuringRow(false);
    setFlashAfterRow(false);
    setHashHubFlashIndex(null);
    setUpdatesCategory(null);
    setUpdatesUtilityType(null);
    setUpdatesTab(null);
    setDuringPanelScrollTarget(null);

    clearHashPanel();
  }, []);

  useEffect(() => {
    if (hashHubFlashIndex == null) return;
    const t = setTimeout(() => setHashHubFlashIndex(null), 4200);
    return () => clearTimeout(t);
  }, [hashHubFlashIndex]);

  const applyHashPanel = useCallback(
    (source = "hashchange") => {
      const hashPanel = getHashPanel();

      if (hashPanel === "before") {
        openBeforePlain(source);
        return;
      }
      if (hashPanel === "during") {
        if (source === "deep_link") setHashHubFlashIndex(0);
        openEmergencyHub(source, 0);
        return;
      }
      if (hashPanel === "after") {
        openAfterPlain(source);
        return;
      }

      if (hashPanel === "before-learn-and-share") {
        openBeforeLearnAndShare(source);
        return;
      }
      if (hashPanel === "before-supply-kits") {
        openBeforeSupplyLists(source);
        return;
      }
      if (
        hashPanel === "before-hazard-prep-checklists" ||
        (hashPanel && hashPanel.startsWith("before-hazard-prep-checklists-"))
      ) {
        openBeforeChecklists(source);
        return;
      }
      if (hashPanel === "before-readiness-roadmap") {
        openRoadmap(source);
        return;
      }
      if (hashPanel === "before-long-term-planning") {
        openRoadmap(source);
        return;
      }

      if (hashPanel === "during-hub") {
        if (source === "deep_link") setHashHubFlashIndex(0);
        openEmergencyHub(source, 0);
        return;
      }
      if (hashPanel === "during-updates") {
        if (source === "deep_link") setHashHubFlashIndex(0);
        openEmergencyHub(source, 0);
        return;
      }
      if (hashPanel === "during-weather") {
        if (source === "deep_link") setHashHubFlashIndex(1);
        openEmergencyHub(source, 1);
        return;
      }
      if (hashPanel === "during-transportation") {
        if (source === "deep_link") setHashHubFlashIndex(2);
        openEmergencyHub(source, 2);
        return;
      }
      if (hashPanel === "during-utilities") {
        if (source === "deep_link") setHashHubFlashIndex(3);
        openEmergencyHub(source, 3);
        return;
      }
      if (hashPanel === "during-aid") {
        if (source === "deep_link") setHashHubFlashIndex(4);
        openEmergencyHub(source, 4);
        return;
      }
      if (hashPanel && hashPanel.startsWith("local-conditions-")) {
        if (source === "deep_link") setHashHubFlashIndex(1);
        openEmergencyHub(source, 1);
        return;
      }

      if (hashPanel === "after-timeline-panel") {
        openTimelinePanel(source);
        return;
      }
      if (hashPanel === "after-recovery-timeline") {
        openAfterTimeline(source);
        return;
      }
      if (hashPanel === "after-mental-health-and-well-being") {
        openAfterMentalHealth(source);
        return;
      }
      if (hashPanel === "after-checklists") {
        openAfterChecklists(source);
        return;
      }
    },
    [
      openBeforePlain,
      openBeforeLearnAndShare,
      openEmergencyHub,
      openAfterPlain,
      openTimelinePanel,
      openRoadmap,
      openBeforeChecklists,
      openBeforeSupplyLists,
      openAfterTimeline,
      openAfterMentalHealth,
      openAfterChecklists,
    ],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hashPanel = getHashPanel();

    if (hashPanel) {
      applyHashPanel("deep_link");
      return;
    }

    const saved = window.localStorage.getItem(STORAGE_KEY) || "";

    setRoadmapOpen(false);
    setOpenHub(false);
    setTimelineOpen(false);

    setActivePanel(saved);

    if (saved === "before") {
      setRoadmapOpen(true);
    } else if (saved === "during") {
      setOpenHub(true);
    } else if (saved === "after") {
      setTimelineOpen(true);
    }
  }, [applyHashPanel]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleHashChange = () => {
      applyHashPanel("hashchange");
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [applyHashPanel]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, activePanel);
  }, [activePanel]);

  const value = {
    roadmapOpen,
    setRoadmapOpen,
    checklistsOpen,
    setChecklistsOpen,
    activePanel,
    setActivePanel,
    sitRepPanelIndex,
    setSitRepPanelIndex,
    openHub,
    setOpenHub,
    openTimeline,
    setTimelineOpen,
    openHubFlag,
    setOpenHubFlag,

    openBeforePlain,
    openBeforeGeneric,
    openBeforeLearnAndShare,
    openRoadmap,
    openBeforeChecklists,
    openBeforeSupplyLists,

    openEmergencyHub,
    openUpdatesUtilitiesWater,

    openAfterPlain,
    openTimelinePanel,
    openAfterMentalHealth,
    openAfterTimeline,
    openAfterChecklists,

    weatherAlertSidebarOpen,
    weatherAlertForSidebar,
    openWeatherAlertSidebar,
    closeWeatherAlertSidebar,

    highlightChecklist,
    setHighlightChecklist,
    highlightRoadmap,
    setHighlightRoadmap,
    highlightSupplyLists,
    setHighlightSupplyLists,
    highlightHubNavIndex,
    setHighlightHubNavIndex,
    hashHubFlashIndex,

    closePanels,
    trackNavigation,
    roadmapCenterRequested,
    setRoadmapCenterRequested,
    beforeScrollRequested,
    setBeforeScrollRequested,
    lastBeforeTarget,
    setLastBeforeTarget,
    beforeActiveTile,
    setBeforeActiveTile,

    duringScrollRequested,
    setDuringScrollRequested,
    duringSubIndex,
    setDuringSubIndex,
    duringPanelScrollRequest,
    duringPanelScrollTarget,

    highlightAfterTimeline,
    setHighlightAfterTimeline,
    highlightAfterTips,
    setHighlightAfterTips,
    highlightAfterChecklists,
    setHighlightAfterChecklists,
    lastAfterTarget,
    setLastAfterTarget,
    afterTargetPanelId,
    setAfterTargetPanelId,
    afterScrollRequested,
    setAfterScrollRequested,

    flashBeforeRow,
    setFlashBeforeRow,
    flashDuringRow,
    setFlashDuringRow,
    flashAfterRow,
    setFlashAfterRow,

    updatesCategory,
    setUpdatesCategory,
    updatesUtilityType,
    setUpdatesUtilityType,
    updatesFocusRequest,
    clearUpdatesNavigation,
    updatesTab,
    setUpdatesTab,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigationContext() {
  return useContext(NavigationContext);
}
